import dayjs from '@/utils/dayjs';
import { OVERDUE_NOTICE_GRACE_PERIOD_DAYS } from '@/app/Invoice';

const DATE_FORMAT = 'YYYY-MM-DD';

// The reminders that make up the follow up process, in the order they are sent.
// Later reminders (3rd and up) are lawful but cannot carry a charge, so they are
// not part of the process we guide the user through.
export const PROCESS_REMINDER_ORDER_NUMBERS = [ 1, 2 ];

export type OverdueStepKey = 'invoiceSent' | 'firstReminder' | 'secondReminder' | 'paid';

// done       - the step has been carried out
// actionable - the step should be carried out now
// waiting    - the step cannot lawfully be carried out yet
// idle       - there is nothing to do for this step
export type OverdueStepState = 'done' | 'actionable' | 'waiting' | 'idle';

export type OverdueActionType = 'createReminder' | 'sendReminder' | 'wait' | 'none';

export interface IProcessOverdueNoticeLog {
  deliveryStatus: string | null;
}

export interface IProcessOverdueNotice {
  id: string;
  orderNumber: number;
  dueDate: string;
  // Must be queried for the process to tell a written reminder from a sent one.
  // When it is missing the reminder counts as not sent.
  overdueNoticeLogs?: IProcessOverdueNoticeLog[] | null;
}

export interface IProcessInvoice {
  dueDate: string;
  paidDate?: string | null;
  invoiceOverdueNotices: IProcessOverdueNotice[];
}

export interface IOverdueStep {
  key: OverdueStepKey;
  state: OverdueStepState;
  orderNumber: number | null;
  // The first date the step may lawfully be carried out, when we can know it.
  availableFrom: string | null;
}

export interface IOverdueAction {
  type: OverdueActionType;
  orderNumber: number | null;
  noticeId: string | null;
  availableFrom: string | null;
}

export interface IOverdueProcess {
  steps: IOverdueStep[];
  action: IOverdueAction;
}

const findNotice = (
  invoice: IProcessInvoice,
  orderNumber: number,
): IProcessOverdueNotice | undefined => {
  return invoice.invoiceOverdueNotices.find(notice => notice.orderNumber === orderNumber);
};

// A reminder counts as sent once at least one mail left without failing.
export const isNoticeSent = (notice: IProcessOverdueNotice): boolean => {
  return (notice.overdueNoticeLogs || []).some(log => log.deliveryStatus !== 'failed');
};

// A reminder may carry a charge once the grace period has passed since the
// previous due date - the invoice's own for the first reminder, the preceding
// reminder's for the ones after it. Mirrors the lawful dates the reminder form
// validates against, see https://lovdata.no/forskrift/1989-07-14-562/§1-1
export const reminderAvailableFrom = (
  invoice: IProcessInvoice,
  orderNumber: number,
): string | null => {
  const previous = orderNumber <= 1 ? null : findNotice(invoice, orderNumber - 1);

  if (orderNumber > 1 && !previous) return null;

  const previousDueDate = previous ? previous.dueDate : invoice.dueDate;

  return dayjs.strict(previousDueDate)
    .add(OVERDUE_NOTICE_GRACE_PERIOD_DAYS, 'days')
    .format(DATE_FORMAT);
};

const buildReminderSteps = (invoice: IProcessInvoice, today: string): IOverdueStep[] => {
  return PROCESS_REMINDER_ORDER_NUMBERS.map((orderNumber, index) => {
    const availableFrom = reminderAvailableFrom(invoice, orderNumber);
    let state: OverdueStepState;

    if (findNotice(invoice, orderNumber)) {
      state = 'done';
    } else if (availableFrom && !dayjs.strict(today).isBefore(availableFrom, 'day')) {
      state = 'actionable';
    } else {
      state = 'waiting';
    }

    return {
      key: index === 0 ? 'firstReminder' : 'secondReminder',
      state,
      orderNumber,
      availableFrom,
    } as IOverdueStep;
  });
};

const nextAction = (
  invoice: IProcessInvoice,
  reminderSteps: IOverdueStep[],
  paid: boolean,
): IOverdueAction => {
  const nothing: IOverdueAction = {
    type: 'none', orderNumber: null, noticeId: null, availableFrom: null,
  };

  if (paid) return nothing;

  // A reminder that was written but never sent is always the most pressing step.
  const unsent = invoice.invoiceOverdueNotices
    .filter(notice => !isNoticeSent(notice))
    .sort((a, b) => a.orderNumber - b.orderNumber)[0];

  if (unsent) {
    return {
      type: 'sendReminder',
      orderNumber: unsent.orderNumber,
      noticeId: unsent.id,
      availableFrom: null,
    };
  }

  const pending = reminderSteps.find(step => step.state !== 'done');

  if (!pending) return nothing;

  return {
    type: pending.state === 'actionable' ? 'createReminder' : 'wait',
    orderNumber: pending.orderNumber,
    noticeId: null,
    availableFrom: pending.state === 'actionable' ? null : pending.availableFrom,
  };
};

// Describes where an overdue invoice stands in the payment reminder process:
// one step per circle in the UI, plus the single next step the user should take.
export const buildOverdueNoticeProcess = (
  invoice: IProcessInvoice,
  today: string = dayjs().format(DATE_FORMAT),
): IOverdueProcess => {
  const reminderSteps = buildReminderSteps(invoice, today);
  const paid = !!invoice.paidDate;
  const remindersDone = reminderSteps.every(step => step.state === 'done');

  const paidState: OverdueStepState = paid ? 'done' : (remindersDone ? 'actionable' : 'idle');

  const steps: IOverdueStep[] = [
    { key: 'invoiceSent', state: 'done', orderNumber: null, availableFrom: null },
    ...reminderSteps,
    { key: 'paid', state: paidState, orderNumber: null, availableFrom: null },
  ];

  return { steps, action: nextAction(invoice, reminderSteps, paid) };
};
